import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { MainLayout } from '@/components/layouts'
import { MainContent } from '@/components/content'
import { SettingSidebar } from '@/components/sidebar'
import { ButtonPrimary } from '@/components/button'
import { LoadingScreen } from '@/components/common'

import { getDuprApi } from '@/app/commons/network/duprApiCore'
import { ROUTE_PROFILE } from '@/constants/Routes'

type ApiError = {
  response?: { data?: { message?: string } }
}

const CELLS = 6

export function VerifyPhonePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAppSelector(userSelector)
  const api = getDuprApi()

  const [params] = useSearchParams()
  const phoneFromQuery = params.get('phone') ?? undefined
  const isoFromQuery = params.get('iso') ?? undefined

  // Canonical inputs
  const phoneE164 = (phoneFromQuery ?? user?.phone ?? '').trim()
  const isoCode = (isoFromQuery ?? user?.isoCode ?? '').trim()

  const [code, setCode] = useState<string>('')
  const [otpSent, setOtpSent] = useState<boolean>(false)
  const [verified, setVerified] = useState<boolean>(false)
  const [resendAt, setResendAt] = useState<number>(0)
  const [now, setNow] = useState<number>(() => Date.now())
  const inputs = useRef<Array<HTMLInputElement | null>>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!otpSent || verified || resendAt <= Date.now()) return
    const id = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(id)
  }, [otpSent, verified, resendAt])

  const secondsLeft = useMemo(() => Math.max(0, Math.ceil((resendAt - now) / 1000)), [resendAt, now])

  const sendOtpMutation = useMutation({
    mutationKey: ['sendPhoneOTP', phoneE164],
    mutationFn: async () => api.user.sendPhoneOTP('v1.0', { phoneNumber: phoneE164 }),
    onError: (e: unknown) => {
      const msg = (e as ApiError).response?.data?.message ?? t('common.genericError', 'Something went wrong')
      setError(msg)
    },
  })

  const verifyOtpMutation = useMutation({
    mutationKey: ['verifyPhoneOTP', phoneE164, code],
    mutationFn: async () =>
      api.user.verifyPhoneNumber('v1.0', {
        isoCode,
        phoneNumber: phoneE164,
        otp: code,
      }),
    onSuccess: (res) => {
      if (res) {
        setVerified(true)
        setCode('')
        toast.success(t('common.phoneVerified', 'Phone number verified'))
        setTimeout(() => navigate(ROUTE_PROFILE), 650)
      } else {
      }
    },
    onError: (e: unknown) => {
      const msg = (e as ApiError).response?.data?.message ?? t('common.invalidCode', 'Invalid code, try again.')
      setCode('')
      inputs.current[0]?.focus()
      setError(msg)
    },
  })

  const onChange = (i: number, v: string) => {
    const digit = v.replace(/\D/g, '').slice(-1)
    const chars = Array.from({ length: CELLS }, (_, k) => code[k] ?? '')
    chars[i] = digit
    const next = chars.join('').slice(0, CELLS)
    setCode(next)
    if (digit && i < CELLS - 1) inputs.current[i + 1]?.focus()
  }

  const onKeyDown = (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return
    const current = code[i] ?? ''
    if (current) {
      const chars = Array.from({ length: CELLS }, (_, k) => code[k] ?? '')
      chars[i] = ''
      setCode(chars.join(''))
      return
    }
    if (i > 0) {
      const chars = Array.from({ length: CELLS }, (_, k) => code[k] ?? '')
      chars[i - 1] = ''
      setCode(chars.join(''))
      inputs.current[i - 1]?.focus()
    }
  }

  const sendNow = async () => {
    if (sendOtpMutation.isLoading || verified) return
    if (!phoneE164) {
      setError(t('common.phoneMissing', 'No phone number to verify.'))
      return
    }
    setError(null)
    try {
      await sendOtpMutation.mutateAsync()
      setOtpSent(true)
      const nextAt = Date.now() + 60_000
      setResendAt(nextAt)
      setNow(Date.now())
      setTimeout(() => inputs.current[0]?.focus(), 0)
    } catch {
      // onError handler sets the error state
    }
  }

  const resend = async () => {
    if (secondsLeft > 0 || sendOtpMutation.isLoading || verified) return
    setError(null)
    try {
      await sendOtpMutation.mutateAsync()
      const nextAt = Date.now() + 60_000
      setResendAt(nextAt)
      setNow(Date.now())
    } catch {
      // onError handler sets the error state
    }
  }

  const submit = async () => {
    if (code.length !== CELLS || verified || verifyOtpMutation.isLoading) return
    setError(null)
    try {
      await verifyOtpMutation.mutateAsync()
    } catch {
      // onError handler sets the error state
    }
  }

  const disabledSend = sendOtpMutation.isLoading || verified || !phoneE164
  const disabledVerify = verifyOtpMutation.isLoading || code.length !== CELLS

  return (
    <MainLayout>
      <SettingSidebar />
      <MainContent>
        <div className="flex w-full flex-col items-center">
          <div className="mb-20 flex w-full flex-col gap-y-6 lg:mb-8 lg:w-[480px]">
            <h1 className="text-2xl font-bold text-white md:text-[32px] md:leading-10">
              {t('common.verifyYourPhone', 'Verify your phone')}
            </h1>

            {/* Display the phone being verified */}
            <div className="rounded-2xl bg-white/10 p-4">
              {phoneE164 ? (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-white/60">
                      {t('common.verifyingNumber', 'Verifying number')}
                    </div>
                    <div className="mt-1 text-lg font-semibold text-white" data-testid="verifying-e164">
                      {phoneE164}
                    </div>
                    {isoCode && (
                      <div className="text-xs text-white/50">
                        {t('common.iso', 'ISO')}: {isoCode}
                      </div>
                    )}
                  </div>
                  <Link
                    to={ROUTE_PROFILE}
                    className="rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-indigo-200 hover:bg-white/20"
                  >
                    {t('common.changeNumber', 'Change number')}
                  </Link>
                </div>
              ) : (
                <div className="text-sm text-red-200">{t('common.phoneMissing', 'No phone number to verify.')}</div>
              )}
            </div>

            <div className="rounded-2xl bg-white/10 p-6">
              <p className="text-sm text-white/80">
                {otpSent
                  ? t('common.sentCodeTo', { phone: phoneE164 })
                  : t('common.sendCodeTo', { phone: phoneE164 || t('common.yourPhone', 'your phone') })}
              </p>

              {verified ? (
                <div className="mt-6 flex flex-col items-center">
                  <div className="text-center text-2xl font-semibold text-white">
                    {t('common.phoneVerified', 'Phone number verified')}
                  </div>
                  <div className="mt-2 text-center text-white/80">{t('common.youreAllSet', 'You’re all set.')}</div>
                </div>
              ) : (
                <div className="mt-6">
                  {!otpSent ? (
                    <>
                      {error && (
                        <p className="mb-3 text-sm font-medium text-red-400">{error}</p>
                      )}
                      <div className="flex justify-start">
                        <ButtonPrimary
                          title={
                            sendOtpMutation.isLoading
                              ? t('common.sending', 'Sending...')
                              : t('common.sendCode', 'Send code')
                          }
                          className="px-6"
                          disabled={disabledSend}
                          onClick={sendNow}
                          type="button"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mt-2 flex items-center justify-center gap-2">
                        {Array.from({ length: CELLS }).map((_, i) => (
                          <input
                            key={i}
                            ref={(el) => (inputs.current[i] = el)}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={1}
                            autoFocus={i === 0}
                            value={code[i] ?? ''}
                            onChange={(e) => onChange(i, e.target.value)}
                            onKeyDown={onKeyDown(i)}
                            className="h-14 w-11 rounded-xl border border-[#D7DCE5] bg-white text-center text-lg outline-none focus:ring-2 focus:ring-indigo-400"
                            aria-label={`Digit ${i + 1}`}
                          />
                        ))}
                      </div>

                      {error && (
                        <p className="mt-3 text-sm font-medium text-red-400">{error}</p>
                      )}

                      <div className="mt-4 flex justify-start">
                        <ButtonPrimary
                          title={
                            verifyOtpMutation.isLoading
                              ? t('common.verifying', 'Verifying...')
                              : t('common.verify', 'Verify')
                          }
                          className="px-6"
                          disabled={disabledVerify}
                          onClick={submit}
                          type="button"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={resend}
                        className={`mt-3 text-sm font-semibold ${
                          secondsLeft > 0 ? 'cursor-not-allowed text-white/40' : 'text-indigo-300 hover:text-indigo-200'
                        }`}
                        disabled={secondsLeft > 0}
                      >
                        {secondsLeft > 0
                          ? t('common.resendIn', { s: secondsLeft })
                          : t('common.resendCode', 'Resend code')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <p className="text-center text-xs text-white/60">
              {t('common.otpDisclaimer', 'Standard messaging rates may apply.')}
            </p>
          </div>
        </div>
      </MainContent>

      {(sendOtpMutation.isLoading || verifyOtpMutation.isLoading) && <LoadingScreen />}
    </MainLayout>
  )
}

export default VerifyPhonePage
